import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Send, Mail, MapPin, ArrowRight, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roleOptions = [
  {
    value: "Defense Contractor (OSC) – We need CMMC readiness or assessment services.",
    label: "Defense Contractor (OSC)",
    description: "We need CMMC readiness or assessment services."
  },
  {
    value: "MSP / IT Provider – We are looking for an RPO/C3PAO partner for our clients.",
    label: "MSP / IT Provider",
    description: "We are looking for an RPO/C3PAO partner for our clients."
  },
  {
    value: "CMMC Professional (Individual) – I am an RP, CCP, or CCA looking for opportunities.",
    label: "CMMC Professional (Individual)",
    description: "I am an RP, CCP, or CCA looking for opportunities."
  },
  {
    value: "C3PAO / RPO – We are interested in a strategic B2B partnership.",
    label: "C3PAO / RPO",
    description: "We are interested in a strategic B2B partnership."
  },
  {
    value: "We are just starting to learn about CMMC.",
    label: "Just Starting",
    description: "We are just starting to learn about CMMC."
  },
];

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    organization: "",
    role_type: [],
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoleChange = (value, checked) => {
    setFormData((prev) => ({
      ...prev,
      role_type: checked
        ? [...prev.role_type, value]
        : prev.role_type.filter((r) => r !== value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.role_type.length === 0) {
      toast.error("Please select at least one role/organization type");
      return;
    }
    
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Thank you for your submission!", {
        description: "Someone will be in contact with you soon.",
      });
      setFormData({ 
        first_name: "", 
        last_name: "", 
        email: "", 
        phone: "", 
        organization: "", 
        role_type: [],
        message: "" 
      });
    } catch (error) {
      toast.error("Failed to send message", {
        description: "Please try again or email us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      ref={ref}
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 to-slate-900" />
      
      {/* Decorative elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-[#D4AF37]/3 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Side - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4">
              Get in Touch
            </span>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
              Ready to Align with Excellence?
            </h2>
            
            <p className="text-slate-400 text-base md:text-lg leading-relaxed mb-8">
              Put comprehensive cybersecurity and CMMC expertise in your corner. 
              Whether you need compliance guidance, security assessments, or 
              strategic advisory, we&apos;re here to help.
            </p>

            {/* Contact Info */}
            <div className="space-y-6 mb-10">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Email us at</p>
                  <a
                    href="mailto:info@1dc.io"
                    className="text-white font-medium hover:text-[#D4AF37] transition-colors"
                  >
                    info@1dc.io
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Headquarters</p>
                  <p className="text-white font-medium">Orlando, Florida</p>
                </div>
              </div>
            </div>

            {/* Calendly CTA */}
            <div className="p-6 rounded-2xl bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20">
              <p className="text-white font-semibold mb-2">
                Prefer a quick call?
              </p>
              <p className="text-slate-400 text-sm mb-4">
                Schedule a 30-minute consultation to discuss your needs.
              </p>
              <Button
                onClick={() => window.open("https://calendly.com/first-defense/30min", "_blank")}
                className="btn-gold px-6 py-3 rounded-full text-sm group"
                data-testid="contact-calendly-button"
              >
                Book a Consultation
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="p-8 rounded-2xl bg-slate-900/50 border border-slate-800 backdrop-blur-sm">
              {isSubmitted ? (
                /* Success Message */
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-white text-2xl font-bold mb-3">
                    Thank You!
                  </h3>
                  <p className="text-slate-400 text-lg mb-6">
                    Thank you for your submission, someone will be in contact with you soon.
                  </p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="border-slate-700 text-slate-300 hover:text-white hover:border-[#D4AF37]"
                    data-testid="contact-submit-another"
                  >
                    Submit Another Message
                  </Button>
                </div>
              ) : (
                /* Contact Form */
                <>
                  <h3 className="text-white text-xl font-bold mb-6">
                    Send us a message
                  </h3>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="first_name" className="text-slate-300">
                          First Name *
                        </Label>
                        <Input
                          id="first_name"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          required
                          placeholder="John"
                          className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                          data-testid="contact-first-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="last_name" className="text-slate-300">
                          Last Name *
                        </Label>
                        <Input
                          id="last_name"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          required
                          placeholder="Smith"
                          className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                          data-testid="contact-last-name-input"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-300">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john@company.gov"
                          className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                          data-testid="contact-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-slate-300">
                          Phone *
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          placeholder="(555) 123-4567"
                          className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                          data-testid="contact-phone-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="organization" className="text-slate-300">
                        Organization/Company *
                      </Label>
                      <Input
                        id="organization"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        required
                        placeholder="Company or Agency Name"
                        className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                        data-testid="contact-organization-input"
                      />
                    </div>

                    {/* Role/Organization Type Checkboxes */}
                    <div className="space-y-3">
                      <Label className="text-slate-300">
                        What best describes your current role or organization? *
                      </Label>
                      <div className="space-y-2">
                        {roleOptions.map((option) => (
                          <div
                            key={option.value}
                            className="flex items-start space-x-3 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50 hover:border-slate-600 transition-colors"
                          >
                            <Checkbox
                              id={option.value}
                              checked={formData.role_type.includes(option.value)}
                              onCheckedChange={(checked) =>
                                handleRoleChange(option.value, checked)
                              }
                              className="mt-0.5 border-slate-600 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                              data-testid={`contact-role-${option.label.toLowerCase().replace(/\s+/g, '-')}`}
                            />
                            <label
                              htmlFor={option.value}
                              className="flex-1 cursor-pointer"
                            >
                              <span className="text-white text-sm font-medium block">
                                {option.label}
                              </span>
                              <span className="text-slate-400 text-xs">
                                {option.description}
                              </span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-slate-300">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Tell us about your project or requirements..."
                        rows={4}
                        className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 resize-none"
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full btn-gold py-6 rounded-xl text-base font-semibold"
                      data-testid="contact-submit-button"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <svg
                            className="animate-spin h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send className="w-5 h-5" />
                          Send Message
                        </span>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
